import { useId } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { cn } from '../ui/utils';
import { bandStyle, type OrganBand } from './organHealth';
import type { OrganScoreHistoryPoint } from '../../api/organHealthApi';

/**
 * Tiny inline trend line for one organ's score card — history, not just
 * today's number. No axes/legend/tooltip; the number on the card already
 * carries the precise value. Colour follows the organ's OWN current band
 * (via `currentColor`, so light/dark both resolve correctly) rather than a
 * fixed hue, so the sparkline always agrees with the card it sits in.
 * Renders a flat neutral placeholder — never a fabricated slope — when
 * there are fewer than two graded points.
 */
export function OrganTrendSparkline({
  points,
  band,
  className,
}: {
  points: OrganScoreHistoryPoint[];
  band: OrganBand;
  className?: string;
}) {
  const gradientId = useId();
  const graded = points.filter((p): p is OrganScoreHistoryPoint & { score: number } => typeof p.score === 'number');
  const accent = bandStyle(band).accent;

  if (graded.length < 2) {
    return (
      <div className={cn('h-8 w-full', className)} aria-hidden="true">
        <div className="mt-3.5 h-px w-full bg-border" />
      </div>
    );
  }

  // History arrives newest-first; a chart reads left (oldest) to right (newest).
  const data = [...graded].reverse().map((p, i) => ({ i, score: p.score }));

  return (
    <div className={cn('h-8 w-full', accent, className)}>
      <span className="sr-only">
        Score trend, oldest to newest: {data.map((d) => d.score).join(', ')}.
      </span>
      <ResponsiveContainer width="100%" height="100%" aria-hidden="true">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.35} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="score"
            stroke="currentColor"
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
